import { useEffect, useRef, useState } from 'react';

const SESSION_KEY = 'lasertag-fireworks-shown';
const DURATION = 4200;
const FADE_START = 3200;
const PIECE_COUNT = 160;

interface Confetto {
  x: number;
  y: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  swayPhase: number;
  swaySpeed: number;
  swayAmplitude: number;
}

const readToken = (name: string, fallback: string): string => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return raw ? `hsl(${raw})` : fallback;
};

/**
 * Einmaliges Konfetti pro Browser-Sitzung (sessionStorage, nicht localStorage).
 * Wird erst gerendert, wenn echte Spieldaten sichtbar sind.
 */
export function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let alreadyShown = true;
    try {
      alreadyShown = sessionStorage.getItem(SESSION_KEY) === '1';
      // Merker sofort setzen — nicht erst nach der Animation.
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      return;
    }
    if (alreadyShown) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const colors = [
      readToken('--primary', 'hsl(271 91% 65%)'),
      readToken('--team-green', 'hsl(150 72% 47%)'),
      readToken('--rank-gold', 'hsl(41 100% 62%)'),
      readToken('--accent', 'hsl(77 100% 62%)'),
      readToken('--team-blue', 'hsl(217 91% 60%)'),
    ];

    const pieces: Confetto[] = [];
    const spawn = () => {
      for (let i = 0; i < PIECE_COUNT; i++) {
        pieces.push({
          // Start oberhalb des Viewports, gestaffelt nach links/rechts geworfen
          x: Math.random() * width,
          y: -20 - Math.random() * height * 0.35,
          vx: (Math.random() - 0.5) * 1.2,
          vy: 1.6 + Math.random() * 2.4,
          width: 6 + Math.random() * 6,
          height: 4 + Math.random() * 5,
          color: colors[i % colors.length],
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.22,
          swayPhase: Math.random() * Math.PI * 2,
          swaySpeed: 0.03 + Math.random() * 0.05,
          swayAmplitude: 0.6 + Math.random() * 1.4,
        });
      }
    };
    spawn();

    const start = performance.now();

    const frame = (now: number) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, width, height);

      const fade =
        elapsed > FADE_START ? Math.max(0, 1 - (elapsed - FADE_START) / (DURATION - FADE_START)) : 1;

      for (const p of pieces) {
        p.swayPhase += p.swaySpeed;
        p.x += p.vx + Math.sin(p.swayPhase) * p.swayAmplitude;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;

        // Unten angekommen? Von oben neu einsetzen, solange die Animation läuft.
        if (p.y > height + 20) {
          if (elapsed < FADE_START - 800) {
            p.y = -20;
            p.x = Math.random() * width;
          } else {
            continue;
          }
        }

        ctx.globalAlpha = 0.95 * fade;
        ctx.fillStyle = p.color;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        // Leichte "Flatter"-Illusion: Höhe oszilliert mit der Sway-Phase
        const flutter = 0.6 + 0.4 * Math.abs(Math.cos(p.swayPhase * 1.7));
        ctx.fillRect(-p.width / 2, (-p.height * flutter) / 2, p.width, p.height * flutter);
        ctx.restore();
      }
      ctx.globalAlpha = 1;

      if (elapsed >= DURATION) {
        setVisible(false);
        return;
      }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      window.removeEventListener('resize', resize);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-40"
    />
  );
}
